/**
 * Clients follow the server's build (docs/deploy.md "Clients follow the server"). A server in
 * same-build mode names its build (commit) in the ws welcome and /v1/config; a web client built
 * from another commit reloads into the server's bundle at once (the deploy already dropped it, and
 * the cookie session resumes it), carrying over what only lives in the page: the unsent chat line
 * and the open panels. A native build (its web assets bundled) can't reload into a new bundle: it
 * plays on while the protocol number matches, else it shows the store-update screen.
 */

export type BuildDecision =
  /** nothing to do: same build, or no check (no build named, or a build-less dev client) */
  | "same"
  /** reload into the server's bundle */
  | "reload"
  /** reloaded twice already for this build: stop, and ask for a manual reload */
  | "stuck"
  /** an admin page: never yank an editor, show a "save, then reload" banner */
  | "banner"
  /** a native build on an incompatible protocol: the store-update screen */
  | "update";

export interface BuildFacts {
  /** this bundle's commit (BUILD.commit; "unknown" in a build without git) */
  mine: string;
  /** the server's required build, when it names one */
  theirs?: string;
  myProtocol: number;
  theirProtocol?: number;
  /** the oldest protocol the server still serves (absent: an older server, `theirProtocol` only) */
  theirMinProtocol?: number;
  native: boolean;
  admin: boolean;
  /** forced reloads already made for `theirs` in the last RELOAD_WINDOW_MS */
  reloads: number;
}

export const MAX_RELOADS = 2;
export const RELOAD_WINDOW_MS = 5 * 60_000;

export function decideBuild(f: BuildFacts): BuildDecision {
  if (f.native) {
    if (f.theirProtocol === undefined) return "same";
    const min = f.theirMinProtocol ?? f.theirProtocol;
    return f.myProtocol > f.theirProtocol || f.myProtocol < min ? "update" : "same";
  }
  if (!f.theirs || !f.mine || f.mine === "unknown" || f.theirs === f.mine) return "same";
  if (f.admin) return "banner";
  return f.reloads >= MAX_RELOADS ? "stuck" : "reload";
}

// ------------------------------------------------------------------ the reload counter

const RELOADS_KEY = "charter.buildReloads";

interface ReloadLog {
  build: string;
  at: number[];
}

function readLog(store: Storage): ReloadLog | null {
  try {
    const v = JSON.parse(store.getItem(RELOADS_KEY) ?? "null") as ReloadLog | null;
    return v && typeof v.build === "string" && Array.isArray(v.at) ? v : null;
  } catch {
    return null;
  }
}

/** Forgets the forced reloads (a player's own Reload starts a fresh count). */
export function clearReloads(store: Storage | null): void {
  try {
    store?.removeItem(RELOADS_KEY);
  } catch {
    // nothing to clear
  }
}

/** Forgets any carry-over (signing out: never hand one account's line to the next). */
export function clearCarried(store: Storage | null): void {
  try {
    store?.removeItem(CARRY_KEY);
  } catch {
    // nothing to clear
  }
}

/** Forced reloads made for `build` within the window. */
export function reloadsFor(store: Storage, build: string, now: number): number {
  const log = readLog(store);
  if (log?.build !== build) return 0;
  return log.at.filter((t) => now - t < RELOAD_WINDOW_MS).length;
}

export function noteReload(store: Storage, build: string, now: number): void {
  const log = readLog(store);
  const at = log?.build === build ? log.at.filter((t) => now - t < RELOAD_WINDOW_MS) : [];
  try {
    store.setItem(RELOADS_KEY, JSON.stringify({ build, at: [...at, now] }));
  } catch {
    // storage full or blocked: the loop guard is lost, the reload still happens
  }
}

// ------------------------------------------------------------------ what a reload carries over

const CARRY_KEY = "charter.carryOver";

/** sessionStorage, or null where reading it throws (some private modes, sandboxed frames). */
export function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export interface CarryOver {
  chat?: string;
  panels?: string[];
}

/** A carry-over older than this is dropped: it belongs to a reload that never reached the game. */
export const CARRY_MAX_AGE_MS = 2 * 60_000;

export function stashCarryOver(store: Storage, c: CarryOver, now = Date.now()): void {
  try {
    store.setItem(CARRY_KEY, JSON.stringify({ ...c, at: now }));
  } catch {
    // nothing carried: a lost chat line, not a broken reload
  }
}

/** Takes (and clears) one field of what the last forced reload carried over. */
export function takeCarried<K extends keyof CarryOver>(
  store: Storage | null,
  key: K,
  now = Date.now(),
): CarryOver[K] {
  if (!store) return undefined;
  try {
    const c = JSON.parse(store.getItem(CARRY_KEY) ?? "null") as
      (CarryOver & { at?: number }) | null;
    if (!c || typeof c.at !== "number" || now - c.at > CARRY_MAX_AGE_MS) {
      if (c) store.removeItem(CARRY_KEY);
      return undefined;
    }
    if (c[key] === undefined) return undefined;
    const rest: CarryOver & { at?: number } = { ...c };
    delete rest[key];
    if (Object.keys(rest).some((k) => k !== "at")) store.setItem(CARRY_KEY, JSON.stringify(rest));
    else store.removeItem(CARRY_KEY);
    return c[key];
  } catch {
    return undefined;
  }
}
